import {
  ApplicationAndCustomerData,
  CustomerAccountType,
  HyperlinkDataType,
  HyperlinkEnv,
} from 'modules/hyperlink/hyperlink.types';
import { ENVIRONMENT_SANDBOX } from 'utils/constants';

export function isArabicLanguageEnabled(languageCode: string | null) {
  return typeof languageCode === 'string' && languageCode.startsWith('ar');
}

export function getAppName(
  data: ApplicationAndCustomerData,
  languageCode: string | null,
) {
  if (
    isArabicLanguageEnabled(languageCode) &&
    data.application.name?.ar &&
    data.application.name.ar.length > 0
  ) {
    return data.application.name.ar;
  }
  return data.application.name.en;
}

export function getAppPurposeStatement(
  data: ApplicationAndCustomerData,
  languageCode: string | null,
) {
  if (
    isArabicLanguageEnabled(languageCode) &&
    data.application.purpose_statement?.ar &&
    data.application.purpose_statement.ar.length > 0
  ) {
    return data.application.purpose_statement.ar;
  }
  return data.application.purpose_statement?.en;
}

export function getAppBenefitStatement(
  data: ApplicationAndCustomerData,
  languageCode: string | null,
) {
  if (
    isArabicLanguageEnabled(languageCode) &&
    data.application.benefit_statement?.ar &&
    data.application.benefit_statement.ar.length > 0
  ) {
    return data.application.benefit_statement.ar;
  }
  return data.application.benefit_statement?.en;
}

export function getLegalName(
  data: ApplicationAndCustomerData,
  languageCode: string | null,
) {
  if (
    isArabicLanguageEnabled(languageCode) &&
    data.application.legal_name?.ar &&
    data.application.legal_name.ar.length > 0
  ) {
    return data.application.legal_name.ar;
  }
  if (
    data.application.legal_name?.en &&
    data.application.legal_name.en.length > 0
  ) {
    return data.application.legal_name.en;
  }
  return getAppName(data, languageCode);
}

export function getLocalisedBankName<
  T extends {
    name: string;
    arabic_name: string | null;
  },
>(data: T, languageCode: string | null) {
  if (
    isArabicLanguageEnabled(languageCode) &&
    data.arabic_name &&
    data.arabic_name.length > 0
  ) {
    return data.arabic_name;
  }
  return data.name;
}

export function getAccountType(data: ApplicationAndCustomerData) {
  if (data.type === HyperlinkDataType.Customer) {
    return (
      data.customer?.account_type ??
      data.application?.account_type ??
      CustomerAccountType.Personal
    );
  }

  return data.application?.account_type ?? CustomerAccountType.Personal;
}

export function getHyperLinkUrl(environment: string, appId: string) {
  const env =
    (process.env.REACT_APP_ENVIRONMENT as keyof typeof HyperlinkEnv) || '';
  const hyperlinkEnv = HyperlinkEnv[env] || 'sa';
  return `https://connect.${hyperlinkEnv}.leantech.me/hyperlink/${appId}?sandbox=${
    environment === ENVIRONMENT_SANDBOX
  }`;
}

export function downloadSVGAsPNG(svgElement: Element | null, fileName: string) {
  try {
    const canvas = document.createElement('canvas');
    const base64doc = window.btoa(
      decodeURIComponent(encodeURIComponent(svgElement?.outerHTML ?? '')),
    );
    const w = '300';
    const h = '300';
    const downloadableImage = document.createElement('img');
    downloadableImage.src = `data:image/svg+xml;base64,${base64doc}`;
    canvas.setAttribute('width', w);
    canvas.setAttribute('height', h);
    const context = canvas.getContext('2d');
    const a = document.createElement('a');

    downloadableImage.onload = () => {
      context?.drawImage(downloadableImage, 0, 0, 300, 300);
      const dataURL = canvas.toDataURL('image/png');
      const my_evt = new MouseEvent('click');
      a.download = fileName;
      a.href = dataURL;
      a.dispatchEvent(my_evt);
    };
  } catch (e) {
    throw new Error('Could not download PNG image');
  }
}
